import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Dialog,
  DialogHeader,
  DialogBody,
  DialogFooter,
  Button,
  Typography,
  Spinner,
  Alert,
  IconButton,
  Tooltip,
  Chip,
  Switch,
  Collapse,
  Tabs,
  TabsHeader,
  Tab,
} from '@material-tailwind/react';
import {
  LinkIcon,
  ClipboardDocumentIcon,
  CheckIcon,
  NoSymbolIcon,
  EyeIcon,
  CalendarIcon,
  ArrowPathIcon,
  ShieldCheckIcon,
  DevicePhoneMobileIcon,
  DeviceTabletIcon,
  ComputerDesktopIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  GlobeAltIcon,
  UsersIcon,
  ArrowTopRightOnSquareIcon,
  ExclamationTriangleIcon,
  DocumentCheckIcon,
} from '@heroicons/react/24/outline';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from 'recharts';

import { Map, Marker, Overlay } from 'pigeon-maps';

import { createShareLink, getShare, getShareAnalytics, getReportPreview, deactivateShare } from '../services/reportApi';
import { friendlyTimeFormatDt } from '../utils';

const SECTION_LABELS = {
  cover: 'Cover',
  tableOfContents: 'Contents',
  about: 'About',
  listingPerformance: 'Listing Performance',
  marketTrends: 'Market Trends',
  onMarketComps: 'Market Comps',
  recentlySoldComps: 'Sold Comps',
  summaryPricing: 'Summary & Pricing',
  valuationHistory: 'Valuation History',
  strategy: 'Services',
  recommendations: 'Recommendations',
  assumptions: 'Thank You',
};

// Sections per report type
const VALUATION_SECTIONS = [
  'cover', 'tableOfContents', 'about', 'marketTrends', 'onMarketComps',
  'recentlySoldComps', 'summaryPricing', 'strategy', 'assumptions',
];

const MARKETING_SECTIONS = [
  'cover', 'tableOfContents', 'listingPerformance', 'marketTrends',
  'onMarketComps', 'recentlySoldComps', 'summaryPricing', 'valuationHistory',
  'recommendations', 'assumptions',
];

// All known sections (fallback)
const ALL_SECTIONS = [
  'cover', 'tableOfContents', 'about', 'listingPerformance', 'marketTrends',
  'onMarketComps', 'recentlySoldComps', 'summaryPricing', 'valuationHistory',
  'strategy', 'recommendations', 'assumptions',
];

// Format active engagement time
function formatActiveTime(seconds) {
  if (!seconds || seconds === 0) return '0 sec';
  if (seconds < 60) return `${seconds} sec`;
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return secs > 0 ? `${mins} min ${secs} sec` : `${mins} min`;
}

// Get engagement level based on active time
function getEngagementLevel(seconds) {
  if (!seconds || seconds < 60) {
    return { label: 'Glanced', color: 'gray' };
  }
  if (seconds < 180) {
    return { label: 'Reviewed', color: 'blue' };
  }
  return { label: 'Deeply engaged', color: 'green' };
}

// Map device type string to icon
function getDeviceIcon(deviceType) {
  switch (deviceType?.toLowerCase()) {
    case 'mobile':
      return <DevicePhoneMobileIcon className="h-4 w-4" />;
    case 'tablet':
      return <DeviceTabletIcon className="h-4 w-4" />;
    case 'desktop':
    default:
      return <ComputerDesktopIcon className="h-4 w-4" />;
  }
}

// Format location from optional { city, region, country } object
function formatLocation(location) {
  if (!location) return null;
  const parts = [location.city, location.region].filter(Boolean);
  return parts.length > 0 ? parts.join(', ') : location.country || null;
}

// Get event style - handles viewType from recentViews
function getEventStyle(event) {
  const viewType = event.viewType || event;
  switch (viewType) {
    case 'link_open':
      return {
        bg: 'bg-gray-400',
        ring: 'ring-gray-200',
        label: 'Request',
        description: 'Link clicked, landed on verification page.'
      };
    case 'verification':
      return {
        bg: 'bg-amber-300',
        ring: 'ring-amber-100',
        label: 'Verifying',
        description: 'Started verification process.'
      };
    case 'code_requested':
      return {
        bg: 'bg-amber-500',
        ring: 'ring-amber-200',
        label: 'Code Sent',
        description: 'Verification code sent to email.'
      };
    case 'verified':
      return {
        bg: 'bg-purple-500',
        ring: 'ring-purple-200',
        label: 'Verified',
        description: 'Email verified successfully.'
      };
    case 'report':
      return {
        bg: 'bg-cyan-500',
        ring: 'ring-cyan-200',
        label: 'Opened',
        description: 'Report page loaded.'
      };
    case 'engaged':
    case 'report_viewed':
      return {
        bg: 'bg-blue-500',
        ring: 'ring-blue-200',
        label: 'Viewed',
        description: 'Viewer engaged with the report by scrolling past the cover page.'
      };
    case 'deactivated':
      return {
        bg: 'bg-red-400',
        ring: 'ring-red-200',
        label: 'Deactivated',
        description: 'Share link was deactivated.'
      };
    default:
      return { bg: 'bg-gray-400', ring: 'ring-gray-200', label: viewType, description: '' };
  }
}

function ShareDialog({ open, onClose, reportId, reportTitle, reportType, readOnly = false }) {
  const [share, setShare] = useState(null);
  const [shareAnalytics, setShareAnalytics] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isDeactivating, setIsDeactivating] = useState(false);
  const [error, setError] = useState(null);
  const [copiedShareId, setCopiedShareId] = useState(null);
  const [requireEmailVerification, setRequireEmailVerification] = useState(true);
  const [analyticsTab, setAnalyticsTab] = useState('engagement');
  const [expandedViewers, setExpandedViewers] = useState({});
  const [selectedPin, setSelectedPin] = useState(null);
  const [selectedShareId, setSelectedShareId] = useState(null);
  const [selectedViewerEmail, setSelectedViewerEmail] = useState(null);
  const [trackingWarningUrl, setTrackingWarningUrl] = useState(null);

  const loadShare = useCallback(async (isRefresh = false) => {
    if (!reportId) return;
    try {
      if (isRefresh) {
        setIsRefreshing(true);
      } else {
        setIsLoading(true);
      }
      setError(null);

      const [shareResult, analyticsResult] = await Promise.allSettled([
        getShare(reportId),
        getShareAnalytics(reportId),
      ]);

      if (shareResult.status === 'fulfilled') {
        setShare(shareResult.value.share || null);
      } else {
        throw shareResult.reason;
      }

      if (analyticsResult.status === 'fulfilled') {
        setShareAnalytics(analyticsResult.value);
      } else {
        setShareAnalytics(null);
      }
    } catch (err) {
      console.error('Failed to load share:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [reportId]);

  const handleRefresh = () => {
    loadShare(true);
  };

  useEffect(() => {
    if (open && reportId) {
      loadShare();
      setCopiedShareId(null);
      setShareAnalytics(null);
      setExpandedViewers({});
      setAnalyticsTab('engagement');
      setSelectedPin(null);
    }
  }, [loadShare, open, reportId]);

  const handleCreateShare = async () => {
    try {
      setIsCreating(true);
      setError(null);
      await createShareLink(reportId, { requireEmailVerification });
      // Refresh to get full share data with analytics
      await loadShare();
      // Reset the toggle to default for next time
      setRequireEmailVerification(true);
    } catch (err) {
      console.error('Failed to create share link:', err);
      setError(err.message);
    } finally {
      setIsCreating(false);
    }
  };

  const handleDeactivate = async () => {
    try {
      setIsDeactivating(true);
      setError(null);
      await deactivateShare(reportId);
      // Reload to get updated share state + preserve analytics history
      await loadShare();
    } catch (err) {
      console.error('Failed to deactivate share:', err);
      setError(err.message);
    } finally {
      setIsDeactivating(false);
    }
  };

  const copyToClipboard = async (url, shareId) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedShareId(shareId);
      setTimeout(() => setCopiedShareId(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  // Derive verified viewers from recentViews (grouped by email)
  const deriveVerifiedViewers = useCallback((recentViews) => {
    const verifiedEvents = (recentViews || []).filter((v) => v.verified && v.email);
    const byEmail = {};
    verifiedEvents.forEach((v) => {
      if (!byEmail[v.email]) {
        byEmail[v.email] = {
          email: v.email,
          viewCount: 0,
          firstViewedAt: v.timestamp,
          lastViewedAt: v.timestamp,
          views: [],
        };
      }
      const viewer = byEmail[v.email];
      viewer.viewCount++;
      viewer.views.push(v);
      if (new Date(v.timestamp) < new Date(viewer.firstViewedAt)) {
        viewer.firstViewedAt = v.timestamp;
      }
      if (new Date(v.timestamp) > new Date(viewer.lastViewedAt)) {
        viewer.lastViewedAt = v.timestamp;
      }
    });
    return Object.values(byEmail);
  }, []);

  // Aggregate analytics across all shares
  const aggregatedAnalytics = useMemo(() => {
    if (!shareAnalytics?.shares?.length) return null;
    const shares = shareAnalytics.shares;

    const allRecentViews = shares
      .flatMap((s) => s.recentViews || [])
      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

    return {
      recentViews: allRecentViews,
      shares,
    };
  }, [shareAnalytics]);

  // Auto-select first share (prefer active) when data loads
  useEffect(() => {
    if (!aggregatedAnalytics?.shares?.length) {
      setSelectedShareId(null);
      return;
    }
    const active = aggregatedAnalytics.shares.find((s) => s.isActive);
    setSelectedShareId((prev) => {
      // Keep current selection if it still exists
      if (prev && aggregatedAnalytics.shares.some((s) => s.shareId === prev)) return prev;
      return active?.shareId || aggregatedAnalytics.shares[0].shareId;
    });
  }, [aggregatedAnalytics]);

  // Reset viewer filter when selected share changes
  useEffect(() => {
    setSelectedViewerEmail(null);
  }, [selectedShareId]);

  // Per-link analytics for the selected share
  const selectedShareData = useMemo(() => {
    if (!aggregatedAnalytics?.shares?.length || !selectedShareId) return null;
    return aggregatedAnalytics.shares.find((s) => s.shareId === selectedShareId) || null;
  }, [aggregatedAnalytics, selectedShareId]);


  // Engagement data for the selected share (from new analytics endpoint)
  const selectedEngagement = useMemo(() => {
    if (!selectedShareData?.engagement) {
      // console.log('DEBUG: No engagement data on selectedShareData', selectedShareData);
      return null;
    }
    // console.log('DEBUG: selectedShareData.engagement:', selectedShareData.engagement);
    if (selectedViewerEmail) {
      return selectedShareData.engagement.byUser?.find((u) => u.email === selectedViewerEmail) || null;
    }
    return selectedShareData.engagement.totals || null;
  }, [selectedShareData?.engagement, selectedViewerEmail]);

  // Process chart data scoped to selected share and viewer
  const chartData = useMemo(() => {
    // Views by date from recentViews — only count actual report views
    const allViews = selectedShareData?.recentViews || [];
    const recentViews = allViews
      .filter((v) => v.viewType === 'report')
      .filter((v) => !selectedViewerEmail || v.email === selectedViewerEmail);

    const viewsByDate = {};
    if (recentViews?.length > 0) {
      recentViews.forEach((v) => {
        const date = new Date(v.timestamp).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
        });
        viewsByDate[date] = (viewsByDate[date] || 0) + 1;
      });
    }
    const views = Object.entries(viewsByDate).map(([date, count]) => ({
      date,
      count,
    }));

    // Section time from engagement data
    const sectionTimes = selectedEngagement?.sectionTimes || {};

    // Debug: log the actual sectionTimes from API
    if (Object.keys(sectionTimes).length > 0) {
      console.log('DEBUG sectionTimes from API:', sectionTimes);
    }

    // Pick the right section list based on report type
    const sectionOrder = reportType === 'marketing_update' ? MARKETING_SECTIONS
      : reportType === 'market_valuation' ? VALUATION_SECTIONS
      : ALL_SECTIONS;

    const knownSections = sectionOrder.map((sectionId) => ({
      section: SECTION_LABELS[sectionId] || sectionId,
      sectionId,
      seconds: sectionTimes[sectionId] || 0,
    }));

    // Include any section IDs from API that aren't in the expected order
    const unknownSectionIds = Object.keys(sectionTimes).filter(
      (id) => !sectionOrder.includes(id)
    );
    const unknownSections = unknownSectionIds.map((sectionId) => ({
      section: SECTION_LABELS[sectionId] || sectionId,
      sectionId,
      seconds: sectionTimes[sectionId] || 0,
    }));

    return { views, sections: [...knownSections, ...unknownSections] };
  }, [selectedShareData?.recentViews, selectedEngagement, selectedViewerEmail, reportType]);

  return (
    <>
    <Dialog open={open} handler={onClose} size="lg">
      <DialogHeader className="flex flex-col items-start">
        <Typography variant="h5">Share Report</Typography>
        <Typography variant="small" color="gray" className="font-normal">
          {reportTitle}
        </Typography>
      </DialogHeader>

      <DialogBody className="max-h-[80vh] overflow-y-auto">
        {error && (
          <Alert color="red" className="mb-4" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {isLoading ? (
          <div className="flex justify-center py-8">
            <Spinner className="h-8 w-8" />
          </div>
        ) : (
          <div className="space-y-4">
            {/* Aggregate summary bar - conversion funnel */}
            {shareAnalytics && (
              <div className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg text-sm">
                <Tooltip content="Times the share link was clicked (landed on verification page)" className="z-[10000]">
                  <div className="flex items-center gap-1.5 cursor-help">
                    <LinkIcon className="h-4 w-4 text-gray-500" />
                    <span className="font-semibold">{shareAnalytics.totalLinkOpens || 0}</span>
                    <span className="text-gray-500">requests</span>
                  </div>
                </Tooltip>
                <span className="text-gray-300">→</span>
                <Tooltip content="Verification codes sent" className="z-[10000]">
                  <div className="flex items-center gap-1.5 cursor-help">
                    <span className="font-semibold">{shareAnalytics.totalCodeRequested || 0}</span>
                    <span className="text-gray-500">codes</span>
                  </div>
                </Tooltip>
                <span className="text-gray-300">→</span>
                <Tooltip content="Report page loaded (after verification if required)" className="z-[10000]">
                  <div className="flex items-center gap-1.5 cursor-help">
                    <span className="font-semibold">{shareAnalytics.totalReportOpened ?? shareAnalytics.totalReportViews ?? 0}</span>
                    <span className="text-gray-500">opens</span>
                  </div>
                </Tooltip>
                <span className="text-gray-300">→</span>
                <Tooltip content="Engaged views where viewer scrolled past the cover page" className="z-[10000]">
                  <div className="flex items-center gap-1.5 cursor-help">
                    <EyeIcon className="h-4 w-4 text-gray-500" />
                    <span className="font-semibold">{shareAnalytics.totalEngagedViews ?? 0}</span>
                    <span className="text-gray-500">viewed</span>
                  </div>
                </Tooltip>
                <Tooltip content="Unique viewers who completed email verification" className="z-[10000]">
                  <div className="flex items-center gap-1.5 ml-2 pl-2 border-l border-gray-300 cursor-help">
                    <ShieldCheckIcon className="h-4 w-4 text-gray-500" />
                    <span className="font-semibold">{shareAnalytics.uniqueViewers || 0}</span>
                    <span className="text-gray-500">verified</span>
                  </div>
                </Tooltip>
                <div className="flex items-center gap-3 ml-2 pl-2 border-l border-gray-300">
                  <Tooltip content="Share links manually deactivated" className="z-[10000]">
                    <div className="flex items-center gap-1.5 cursor-help">
                      <NoSymbolIcon className={`h-4 w-4 ${(shareAnalytics.totalDeactivated || 0) > 0 ? 'text-orange-500' : 'text-gray-400'}`} />
                      <span className={`font-semibold ${(shareAnalytics.totalDeactivated || 0) > 0 ? 'text-orange-500' : 'text-gray-500'}`}>
                        {shareAnalytics.totalDeactivated || 0}
                      </span>
                      <span className={(shareAnalytics.totalDeactivated || 0) > 0 ? 'text-orange-500' : 'text-gray-500'}>deactivated</span>
                    </div>
                  </Tooltip>
                  <Tooltip content="Share links that have expired" className="z-[10000]">
                    <div className="flex items-center gap-1.5 cursor-help">
                      <span className={`font-semibold ${(shareAnalytics.totalExpired || 0) > 0 ? 'text-red-500' : 'text-gray-500'}`}>
                        {shareAnalytics.totalExpired || 0}
                      </span>
                      <span className={(shareAnalytics.totalExpired || 0) > 0 ? 'text-red-500' : 'text-gray-500'}>expired</span>
                    </div>
                  </Tooltip>
                </div>
                <div className="ml-auto">
                  <Tooltip content="Refresh" className="z-[10000]">
                    <IconButton variant="text" size="sm" onClick={handleRefresh} disabled={isRefreshing}>
                      <ArrowPathIcon className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                    </IconButton>
                  </Tooltip>
                </div>
              </div>
            )}

            {/* Share Links */}
            <div className="p-4 border rounded-lg">
              <div className="flex items-center gap-2 mb-3">
                <LinkIcon className="h-5 w-5 text-gray-600" />
                <Typography variant="h6" color="blue-gray">
                  Share Links
                </Typography>
              </div>

              <div className="space-y-2">
                {(aggregatedAnalytics?.shares || []).map((s) => {
                  const isExpired = !!s.expiresAt && new Date(s.expiresAt) < new Date();
                  const isActive = s.isActive && !isExpired;
                  const isSelected = s.shareId === selectedShareId;
                  return (
                    <div
                      key={s.shareId}
                      className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                        isSelected
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 bg-white hover:border-blue-300 hover:bg-gray-50'
                      }`}
                      onClick={() => setSelectedShareId(s.shareId)}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <Chip
                            size="sm"
                            value={isActive ? 'Active' : isExpired ? 'Expired' : 'Deactivated'}
                            color={isActive ? 'green' : 'gray'}
                          />
                          {s.requireEmailVerification && (
                            <Chip
                              size="sm"
                              value="Email Protected"
                              color="blue"
                              variant="ghost"
                              icon={<ShieldCheckIcon className="h-3 w-3" />}
                            />
                          )}
                        </div>
                        <div className="flex items-center gap-2 text-xs flex-wrap">
                          <span className="text-gray-500">{s.totalLinkOpens || 0} requests</span>
                          <span className="text-gray-300">→</span>
                          <span className="text-gray-500">{s.totalCodeRequested || 0} codes</span>
                          <span className="text-gray-300">→</span>
                          <span className="text-gray-500">{s.totalReportOpened ?? s.totalReportViews ?? 0} opens</span>
                          <span className="text-gray-300">→</span>
                          {(() => {
                            const views = s.totalEngagedViews ?? 0;
                            return views > 0 ? (
                              <div className="flex items-center gap-1">
                                <EyeIcon className="h-3.5 w-3.5 text-green-500" />
                                <span className="font-medium text-green-600">
                                  {views} {views === 1 ? 'view' : 'views'}
                                </span>
                              </div>
                            ) : (
                              <span className="text-gray-400">0 views</span>
                            );
                          })()}
                          {(s.uniqueViewers || 0) > 0 && (
                            <span className="text-gray-500">
                              · {s.uniqueViewers} verified
                            </span>
                          )}
                          <span className={(s.totalDeactivated || 0) > 0 ? 'text-orange-500' : 'text-gray-400'}>
                            · {s.totalDeactivated || 0} deactivated
                          </span>
                          <span className={(s.totalExpired || 0) > 0 ? 'text-red-500' : 'text-gray-400'}>
                            · {s.totalExpired || 0} expired
                          </span>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 mt-2">
                        <span className="flex-1 text-sm font-mono text-gray-600 truncate select-none">
                          {s.shareUrl}
                        </span>
                        <Tooltip content="Preview (no tracking)" className="z-[10000]">
                          <IconButton
                            variant="outlined"
                            size="sm"
                            onClick={async (e) => {
                              e.stopPropagation();
                              try {
                                const html = await getReportPreview(reportId);
                                const blob = new Blob([html], { type: 'text/html' });
                                window.open(URL.createObjectURL(blob), '_blank');
                              } catch (err) {
                                console.error('Preview error:', err);
                                setError(err.message);
                              }
                            }}
                          >
                            <EyeIcon className="h-4 w-4" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip content={readOnly ? 'View only' : 'Open link (affects tracking)'} className="z-[10000]">
                          <IconButton
                            variant="outlined"
                            size="sm"
                            color="amber"
                            disabled={readOnly}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!readOnly) setTrackingWarningUrl(s.shareUrl);
                            }}
                          >
                            <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip content={readOnly ? 'View only' : (copiedShareId === s.shareId ? 'Copied!' : 'Copy link')} className="z-[10000]">
                          <IconButton
                            variant="outlined"
                            size="sm"
                            onClick={(e) => { e.stopPropagation(); if (!readOnly) copyToClipboard(s.shareUrl, s.shareId); }}
                            disabled={readOnly}
                          >
                            {copiedShareId === s.shareId ? (
                              <CheckIcon className="h-4 w-4 text-green-500" />
                            ) : (
                              <ClipboardDocumentIcon className="h-4 w-4" />
                            )}
                          </IconButton>
                        </Tooltip>
                        <Tooltip content={readOnly ? 'View only' : (isActive ? 'Deactivate' : isExpired ? 'Expired' : 'Deactivated')} className="z-[10000]">
                          <IconButton
                            variant="outlined"
                            size="sm"
                            color={isActive ? 'red' : 'gray'}
                            onClick={isActive && !readOnly ? (e) => { e.stopPropagation(); handleDeactivate(); } : undefined}
                            disabled={!isActive || isDeactivating || readOnly}
                          >
                            {isDeactivating && isActive ? (
                              <Spinner className="h-4 w-4" />
                            ) : (
                              <NoSymbolIcon className="h-4 w-4" />
                            )}
                          </IconButton>
                        </Tooltip>
                      </div>

                      <div className="flex items-center gap-2 mt-1.5 text-xs text-gray-400">
                        <CalendarIcon className="h-3.5 w-3.5" />
                        <span>Created {formatDate(s.createdAt)}</span>
                        {s.expiresAt && (
                          <span className={isExpired ? 'text-red-400' : ''}>
                            · {isExpired ? 'Expired' : 'Expires'} {formatDate(s.expiresAt)}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Create new share */}
              {!(share?.isActive && !(share?.expiresAt && new Date(share.expiresAt) < new Date())) && !readOnly && (
                <div className="mt-3 p-3 rounded-lg border border-dashed border-gray-300 bg-white">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <ShieldCheckIcon className="h-4 w-4 text-gray-500" />
                      <Typography variant="small" className="font-medium text-gray-600">
                        Require email verification
                      </Typography>
                    </div>
                    <Switch
                      checked={requireEmailVerification}
                      onChange={(e) => setRequireEmailVerification(e.target.checked)}
                      color="blue"
                    />
                  </div>
                  <Button
                    onClick={handleCreateShare}
                    disabled={isCreating}
                    className="flex items-center gap-2 mt-2"
                    size="sm"
                    fullWidth
                  >
                    {isCreating ? (
                      <Spinner className="h-4 w-4" />
                    ) : (
                      <LinkIcon className="h-4 w-4" />
                    )}
                    Generate New Share Link
                  </Button>
                </div>
              )}
            </div>

            {/* Per-link analytics */}
            {selectedShareData && (
              <div className="p-4 border rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <Typography variant="small" className="font-semibold text-gray-700">
                    Analytics for selected link
                  </Typography>
                  <Tabs value={analyticsTab} className="w-auto">
                    <TabsHeader className="bg-gray-100 p-1">
                      <Tab value="engagement" onClick={() => setAnalyticsTab('engagement')} className="px-4 py-1.5">
                        <Typography variant="small" className="font-medium">
                          Engagement
                        </Typography>
                      </Tab>
                      <Tab value="viewers" onClick={() => setAnalyticsTab('viewers')} className="px-4 py-1.5">
                        <Typography variant="small" className="font-medium">
                          Viewers
                        </Typography>
                      </Tab>
                    </TabsHeader>
                  </Tabs>
                </div>

                {/* Activity Timeline */}
                {selectedShareData?.recentViews?.length > 0 && (
                  <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                    <Typography variant="small" className="font-medium text-gray-600 mb-2">
                      Activity Timeline
                    </Typography>
                    <div className="relative py-2 overflow-x-auto">
                      <div className="absolute top-1/2 left-0 right-0 h-0.5 bg-gray-300 -translate-y-1/2" />
                      <div className="relative flex items-center gap-2 w-max min-w-full">
                        {[...selectedShareData.recentViews]
                          .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp))
                          .map((event, idx) => {
                            const style = getEventStyle(event);
                            return (
                              <Tooltip
                                key={idx}
                                content={
                                  <div className="p-1 text-xs max-w-[220px]">
                                    <div className="font-semibold">{style.label}</div>
                                    {style.description && (
                                      <div className="opacity-70 text-[10px] mb-1">{style.description}</div>
                                    )}
                                    <div className="opacity-80 border-t border-gray-600 pt-1 mt-1">
                                      {event.timestamp ? new Date(event.timestamp).toLocaleString() : '-'}
                                    </div>
                                    {event.email && event.email !== 'anonymous' && (
                                      <div className="opacity-80">{event.email}</div>
                                    )}
                                    {event.verificationMethod && (
                                      <div className="opacity-70 text-[10px]">
                                        {event.verificationMethod === 'cached' ? '(Return visit)' : '(First-time verification)'}
                                      </div>
                                    )}
                                    {event.location && (event.location.city || event.location.country) && (
                                      <div className="opacity-70 text-[10px] mt-1">
                                        📍 {[event.location.city, event.location.region, event.location.country].filter(Boolean).join(', ')}
                                      </div>
                                    )}
                                  </div>
                                }
                                className="z-[10000]"
                              >
                                <div
                                  className={`relative z-10 w-6 h-6 rounded-full flex items-center justify-center cursor-pointer transition-transform hover:scale-125 ${style.bg} ring-2 ${style.ring}`}
                                >
                                  {event.viewType === 'link_open' && (
                                    <span className="text-white text-[10px] font-bold">↗</span>
                                  )}
                                  {event.viewType === 'verification' && (
                                    <span className="text-white text-[10px] font-bold">...</span>
                                  )}
                                  {event.viewType === 'code_requested' && (
                                    <span className="text-white text-[10px] font-bold">#</span>
                                  )}
                                  {event.viewType === 'verified' && (
                                    <span className="text-white text-[10px] font-bold">✓</span>
                                  )}
                                  {event.viewType === 'report' && (
                                    <DocumentCheckIcon className="h-3 w-3 text-white" />
                                  )}
                                  {(event.viewType === 'engaged' || event.viewType === 'report_viewed') && (
                                    <EyeIcon className="h-3 w-3 text-white" />
                                  )}
                                  {event.viewType === 'deactivated' && (
                                    <NoSymbolIcon className="h-3 w-3 text-white" />
                                  )}
                                </div>
                              </Tooltip>
                            );
                          })}
                      </div>
                    </div>
                    {/* Timeline legend */}
                    <div className="flex flex-wrap items-center gap-3 mt-2 text-xs">
                      <div className="flex items-center gap-1">
                        <div className="w-2.5 h-2.5 rounded-full bg-gray-400" />
                        <span className="text-gray-500">Request</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <div className="w-2.5 h-2.5 rounded-full bg-amber-500" />
                        <span className="text-gray-500">Code Sent</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <div className="w-2.5 h-2.5 rounded-full bg-purple-500" />
                        <span className="text-gray-500">Verified</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <div className="w-2.5 h-2.5 rounded-full bg-cyan-500" />
                        <span className="text-gray-500">Opened</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <div className="w-2.5 h-2.5 rounded-full bg-blue-500" />
                        <span className="text-gray-500">Viewed</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Engagement tab */}
                {analyticsTab === 'engagement' && (
                  <div>
                    {(() => {
                      const verifiedViewers = deriveVerifiedViewers(selectedShareData.recentViews);
                      return verifiedViewers.length > 0 ? (
                        <div className="mb-4">
                          <select
                            value={selectedViewerEmail || ''}
                            onChange={(e) => setSelectedViewerEmail(e.target.value || null)}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                          >
                            <option value="">All viewers</option>
                            {verifiedViewers.map((v) => (
                              <option key={v.email} value={v.email}>{v.email}</option>
                            ))}
                          </select>
                        </div>
                      ) : null;
                    })()}
                    <div className="grid grid-cols-4 gap-3 mb-4">
                      <div className="text-center p-3 bg-gray-50 rounded-lg">
                        <Typography variant="small" color="gray" className="mb-1">
                          Active Reading
                        </Typography>
                        <Typography variant="h6" color="blue-gray" className="font-semibold">
                          {selectedEngagement?.totalActiveSeconds > 0
                            ? formatActiveTime(selectedEngagement.totalActiveSeconds)
                            : '-'}
                        </Typography>
                        {selectedEngagement?.totalActiveSeconds > 0 && (
                          <Chip
                            size="sm"
                            value={getEngagementLevel(selectedEngagement.totalActiveSeconds).label}
                            color={getEngagementLevel(selectedEngagement.totalActiveSeconds).color}
                            className="mt-1"
                          />
                        )}
                      </div>
                      <div className="text-center p-3 bg-gray-50 rounded-lg">
                        <Typography variant="small" color="gray" className="mb-1">
                          Sections Viewed
                        </Typography>
                        <Typography variant="h6" color="blue-gray" className="font-semibold">
                          {selectedEngagement?.sectionsViewed?.length || 0} of {(reportType === 'marketing_update' ? MARKETING_SECTIONS : reportType === 'market_valuation' ? VALUATION_SECTIONS : ALL_SECTIONS).length}
                        </Typography>
                      </div>
                      {(() => {
                        // Derive verified viewers from recentViews
                        const viewers = deriveVerifiedViewers(selectedShareData.recentViews);
                        let firstViewed = 'Not yet';
                        let lastViewed = 'Not yet';

                        if (selectedViewerEmail) {
                          const viewer = viewers.find((v) => v.email === selectedViewerEmail);
                          if (viewer) {
                            firstViewed = viewer.firstViewedAt ? friendlyTimeFormatDt(viewer.firstViewedAt) : 'Not yet';
                            lastViewed = viewer.lastViewedAt ? friendlyTimeFormatDt(viewer.lastViewedAt) : 'Not yet';
                          }
                        } else if (viewers.length > 0) {
                          // For "All viewers", find earliest first and latest last
                          const firstDates = viewers.filter((v) => v.firstViewedAt).map((v) => new Date(v.firstViewedAt));
                          const lastDates = viewers.filter((v) => v.lastViewedAt).map((v) => new Date(v.lastViewedAt));
                          if (firstDates.length > 0) {
                            firstViewed = friendlyTimeFormatDt(new Date(Math.min(...firstDates)).toISOString());
                          }
                          if (lastDates.length > 0) {
                            lastViewed = friendlyTimeFormatDt(new Date(Math.max(...lastDates)).toISOString());
                          }
                        }

                        return (
                          <>
                            <div className="text-center p-3 bg-gray-50 rounded-lg">
                              <Typography variant="small" color="gray" className="mb-1">
                                First Viewed
                              </Typography>
                              <Typography variant="small" className="font-medium">
                                {firstViewed}
                              </Typography>
                            </div>
                            <div className="text-center p-3 bg-gray-50 rounded-lg">
                              <Typography variant="small" color="gray" className="mb-1">
                                Last Viewed
                              </Typography>
                              <Typography variant="small" className="font-medium">
                                {lastViewed}
                              </Typography>
                            </div>
                          </>
                        );
                      })()}
                    </div>

                    {chartData.views.length > 0 && (
                      <div className="mb-4">
                        <Typography variant="small" color="gray" className="mb-2">
                          Views Over Time
                        </Typography>
                        <div className="h-32 w-full">
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={chartData.views}>
                              <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} width={30} />
                              <RechartsTooltip />
                              <Bar dataKey="count" fill="#4F46E5" radius={[4, 4, 0, 0]} />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    )}

                    {chartData.sections.some((s) => s.seconds > 0) ? (
                      <div>
                        <Typography variant="small" color="gray" className="mb-2">
                          Time Per Section (seconds)
                        </Typography>
                        <div className="h-64 w-full">
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={chartData.sections} layout="vertical" margin={{ left: 10 }}>
                              <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                              <YAxis
                                dataKey="section"
                                type="category"
                                tick={{ fontSize: 10 }}
                                width={100}
                                interval={0}
                              />
                              <RechartsTooltip formatter={(value) => [`${value}s`, 'Time']} />
                              <Bar dataKey="seconds" fill="#10B981" radius={[0, 4, 4, 0]} />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-6 text-gray-500 border rounded-lg bg-gray-50">
                        <Typography variant="small" className="font-medium mb-1">
                          No section engagement data
                        </Typography>
                        <Typography variant="small" color="gray">
                          Detailed reading time by section will appear after viewers spend time reading the report
                        </Typography>
                      </div>
                    )}
                  </div>
                )}

                {/* Viewers tab */}
                {analyticsTab === 'viewers' && (
                  <div>
                    {/* Viewer location map */}
                    {(() => {
                      const rawGeoViews = (selectedShareData.recentViews || []).filter(
                        (v) => v.location?.lat != null && v.location?.lon != null
                      );
                      if (rawGeoViews.length === 0) return null;

                      // Offset overlapping pins so they don't stack
                      const coordCounts = {};
                      const geoViews = rawGeoViews.map((v) => {
                        const key = `${v.location.lat},${v.location.lon}`;
                        coordCounts[key] = (coordCounts[key] || 0);
                        const index = coordCounts[key]++;
                        if (index === 0) return v;
                        const angle = (index * 2.4); // golden angle spread
                        const offset = 0.002 * index;
                        return {
                          ...v,
                          location: {
                            ...v.location,
                            lat: v.location.lat + offset * Math.cos(angle),
                            lon: v.location.lon + offset * Math.sin(angle),
                          },
                        };
                      });

                      const lats = geoViews.map((v) => v.location.lat);
                      const lons = geoViews.map((v) => v.location.lon);
                      let center, zoom;
                      if (geoViews.length === 1) {
                        center = [lats[0], lons[0]];
                        zoom = 10;
                      } else {
                        const minLat = Math.min(...lats);
                        const maxLat = Math.max(...lats);
                        const minLon = Math.min(...lons);
                        const maxLon = Math.max(...lons);
                        center = [(minLat + maxLat) / 2, (minLon + maxLon) / 2];
                        const latSpread = maxLat - minLat;
                        const lonSpread = maxLon - minLon;
                        const spread = Math.max(latSpread, lonSpread);
                        if (spread < 0.5) zoom = 11;
                        else if (spread < 2) zoom = 8;
                        else if (spread < 10) zoom = 5;
                        else if (spread < 40) zoom = 4;
                        else zoom = 2;
                      }

                      return (
                        <div className="mb-4 rounded-lg border overflow-hidden">
                          <Map defaultCenter={center} defaultZoom={zoom} height={200} onClick={() => setSelectedPin(null)}>
                            {geoViews.map((view, idx) => (
                              <Marker
                                key={idx}
                                anchor={[view.location.lat, view.location.lon]}
                                color={view.verified ? '#22c55e' : '#9ca3af'}
                                width={30}
                                onClick={() => setSelectedPin(view)}
                              />
                            ))}
                            {selectedPin?.location?.lat != null && (
                              <Overlay
                                anchor={[selectedPin.location.lat, selectedPin.location.lon]}
                                offset={[0, 30]}
                              >
                                <div className="bg-white rounded-lg shadow-lg border p-2 text-xs whitespace-nowrap">
                                  <div className="font-medium">
                                    {formatLocation(selectedPin.location) || 'Unknown location'}
                                  </div>
                                  <div className="text-gray-600 flex items-center gap-1 mt-0.5">
                                    {selectedPin.verified ? (
                                      <>
                                        <ShieldCheckIcon className="h-3 w-3 text-green-500" />
                                        {selectedPin.email}
                                      </>
                                    ) : (
                                      <span className="text-gray-400">Anonymous</span>
                                    )}
                                  </div>
                                  <div className="text-gray-500 flex items-center gap-1 mt-0.5">
                                    {getDeviceIcon(selectedPin.deviceType)}
                                    <span>{selectedPin.browser || '-'}</span>
                                  </div>
                                  <div className="text-gray-400 mt-0.5">
                                    {friendlyTimeFormatDt(selectedPin.timestamp)}
                                  </div>
                                </div>
                              </Overlay>
                            )}
                          </Map>
                        </div>
                      );
                    })()}

                    {/* Verified viewers */}
                    {(() => {
                      const verifiedViewers = deriveVerifiedViewers(selectedShareData.recentViews);
                      const anonymousViews = (selectedShareData.recentViews || []).filter((v) => !v.verified);

                      if (verifiedViewers.length === 0 && anonymousViews.length === 0) {
                        return (
                          <div className="text-center py-8 text-gray-500">
                            <Typography variant="small">No viewer data yet</Typography>
                          </div>
                        );
                      }

                      return (
                        <div className="space-y-2">
                          {/* Verified viewers */}
                          {verifiedViewers.map((viewer) => (
                            <div key={viewer.email} className="border rounded-lg">
                              <button
                                className="w-full flex items-center justify-between p-3 hover:bg-gray-50 transition-colors text-left"
                                onClick={() =>
                                  setExpandedViewers((prev) => ({
                                    ...prev,
                                    [viewer.email]: !prev[viewer.email],
                                  }))
                                }
                              >
                                <div className="flex items-center gap-2">
                                  <ShieldCheckIcon className="h-4 w-4 text-green-500" />
                                  <Typography variant="small" className="font-medium">
                                    {viewer.email}
                                  </Typography>
                                </div>
                                <div className="flex items-center gap-3">
                                  <Typography variant="small" color="gray">
                                    {viewer.viewCount} {viewer.viewCount === 1 ? 'view' : 'views'}
                                  </Typography>
                                  <Typography variant="small" color="gray">
                                    {friendlyTimeFormatDt(viewer.lastViewedAt)}
                                  </Typography>
                                  {expandedViewers[viewer.email] ? (
                                    <ChevronUpIcon className="h-4 w-4 text-gray-400" />
                                  ) : (
                                    <ChevronDownIcon className="h-4 w-4 text-gray-400" />
                                  )}
                                </div>
                              </button>

                              <Collapse open={expandedViewers[viewer.email] || false}>
                                <div className="px-3 pb-3 pt-0">
                                  <div className="text-xs text-gray-500 mb-2">
                                    First viewed: {friendlyTimeFormatDt(viewer.firstViewedAt)}
                                  </div>
                                  {viewer.views?.length > 0 && (
                                    <table className="w-full text-sm">
                                      <thead>
                                        <tr className="border-b border-gray-100">
                                          <th className="text-left py-1 text-gray-500 font-medium">Time</th>
                                          <th className="text-left py-1 text-gray-500 font-medium">Type</th>
                                          <th className="text-left py-1 text-gray-500 font-medium">Auth</th>
                                          <th className="text-left py-1 text-gray-500 font-medium">Device</th>
                                          <th className="text-left py-1 text-gray-500 font-medium">Browser</th>
                                          <th className="text-left py-1 text-gray-500 font-medium">IP</th>
                                          <th className="text-left py-1 text-gray-500 font-medium">Location</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {viewer.views.map((view, idx) => (
                                          <tr key={idx} className="border-b border-gray-50 last:border-0">
                                            <td className="py-1.5 text-gray-600 whitespace-nowrap">
                                              {friendlyTimeFormatDt(view.timestamp)}
                                            </td>
                                            <td className="py-1.5 text-gray-500 text-xs">
                                              {view.viewType || '-'}
                                            </td>
                                            <td className="py-1.5">
                                              {view.verificationMethod === 'email' ? (
                                                <Chip size="sm" value="email" color="green" variant="ghost" className="text-xs" />
                                              ) : view.verificationMethod === 'cached' ? (
                                                <Chip size="sm" value="cached" color="blue" variant="ghost" className="text-xs" />
                                              ) : (
                                                <span className="text-gray-400">-</span>
                                              )}
                                            </td>
                                            <td className="py-1.5">
                                              <div className="flex items-center gap-1 text-gray-600">
                                                {getDeviceIcon(view.deviceType)}
                                                <span className="capitalize">{view.deviceType || 'desktop'}</span>
                                              </div>
                                            </td>
                                            <td className="py-1.5 text-gray-600">
                                              {view.browser || '-'}
                                            </td>
                                            <td className="py-1.5 text-gray-500 font-mono text-xs">
                                              {view.ip || '-'}
                                            </td>
                                            <td className="py-1.5 text-gray-600">
                                              {formatLocation(view.location) || '-'}
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  )}
                                </div>
                              </Collapse>
                            </div>
                          ))}

                          {/* Anonymous views */}
                          {anonymousViews.length > 0 && (
                            <div className="border rounded-lg">
                              <button
                                className="w-full flex items-center justify-between p-3 hover:bg-gray-50 transition-colors text-left"
                                onClick={() =>
                                  setExpandedViewers((prev) => ({
                                    ...prev,
                                    _anonymous: !prev._anonymous,
                                  }))
                                }
                              >
                                <div className="flex items-center gap-2">
                                  <UsersIcon className="h-4 w-4 text-gray-400" />
                                  <Typography variant="small" className="font-medium text-gray-500">
                                    Anonymous
                                  </Typography>
                                </div>
                                <div className="flex items-center gap-3">
                                  <Typography variant="small" color="gray">
                                    {anonymousViews.length} {anonymousViews.length === 1 ? 'link open' : 'link opens'}
                                  </Typography>
                                  {expandedViewers._anonymous ? (
                                    <ChevronUpIcon className="h-4 w-4 text-gray-400" />
                                  ) : (
                                    <ChevronDownIcon className="h-4 w-4 text-gray-400" />
                                  )}
                                </div>
                              </button>

                              <Collapse open={expandedViewers._anonymous || false}>
                                <div className="px-3 pb-3 pt-0">
                                  <table className="w-full text-sm">
                                    <thead>
                                      <tr className="border-b border-gray-100">
                                        <th className="text-left py-1 text-gray-500 font-medium">Time</th>
                                        <th className="text-left py-1 text-gray-500 font-medium">Type</th>
                                        <th className="text-left py-1 text-gray-500 font-medium">IP</th>
                                        <th className="text-left py-1 text-gray-500 font-medium">Location</th>
                                        <th className="text-left py-1 text-gray-500 font-medium">Device</th>
                                        <th className="text-left py-1 text-gray-500 font-medium">Browser</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {anonymousViews.map((view, idx) => (
                                        <tr key={idx} className="border-b border-gray-50 last:border-0">
                                          <td className="py-1.5 text-gray-600 whitespace-nowrap">
                                            {friendlyTimeFormatDt(view.timestamp)}
                                          </td>
                                          <td className="py-1.5 text-gray-500 text-xs">
                                            <div>{view.viewType || '-'}</div>
                                            {view.viewType === 'code_requested' && view.email && (
                                              <div className="text-gray-600 truncate max-w-[150px]" title={view.email}>
                                                {view.email}
                                              </div>
                                            )}
                                          </td>
                                          <td className="py-1.5 text-gray-500 font-mono text-xs">
                                            {view.ip || '-'}
                                          </td>
                                          <td className="py-1.5 text-gray-600">
                                            {formatLocation(view.location) ? (
                                              <div className="flex items-center gap-1">
                                                <GlobeAltIcon className="h-3.5 w-3.5 text-gray-400" />
                                                <span>{formatLocation(view.location)}</span>
                                              </div>
                                            ) : (
                                              <span className="text-gray-400">-</span>
                                            )}
                                          </td>
                                          <td className="py-1.5">
                                            <div className="flex items-center gap-1 text-gray-600">
                                              {getDeviceIcon(view.deviceType)}
                                              <span className="capitalize">{view.deviceType || 'desktop'}</span>
                                            </div>
                                          </td>
                                          <td className="py-1.5 text-gray-600">
                                            {view.browser || '-'}
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </Collapse>
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>
            )}

          </div>
        )}
      </DialogBody>

      <DialogFooter>
        <Button variant="outlined" onClick={onClose}>
          Close
        </Button>
      </DialogFooter>
    </Dialog>

    {/* Tracking Warning Dialog */}
    <Dialog open={!!trackingWarningUrl} handler={() => setTrackingWarningUrl(null)} size="sm">
      <DialogHeader className="flex items-center gap-2">
        <ExclamationTriangleIcon className="h-6 w-6 text-amber-500" />
        <span>Tracking Warning</span>
      </DialogHeader>
      <DialogBody>
        <Typography color="blue-gray" className="mb-4">
          Opening this link will be recorded in the analytics and may affect your tracking data.
        </Typography>
        <Typography variant="small" color="gray">
          To view the report without affecting analytics, use the <strong>Preview</strong> button (eye icon) instead.
        </Typography>
      </DialogBody>
      <DialogFooter className="flex gap-2">
        <Button variant="outlined" onClick={() => setTrackingWarningUrl(null)}>
          Cancel
        </Button>
        <Button
          color="amber"
          onClick={() => {
            window.open(trackingWarningUrl, '_blank');
            setTrackingWarningUrl(null);
          }}
        >
          Open Anyway
        </Button>
      </DialogFooter>
    </Dialog>
    </>
  );
}

export default ShareDialog;
